import { useCallback, useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { ccApi, errMsg, fmtTs } from "./ccApi";
import { ActionBtn, Empty, Pill, Section } from "./ccUi";
import ReasonDialog from "./ReasonDialog";
import { captureAndUpload } from "./evidence";

const RESOLUTIONS = ["dismissed", "content_hidden", "warned", "restricted", "suspended", "banned", "escalated"];

export default function ContentTab({ target, setTarget, me }) {
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const [content, setContent] = useState(null);
  const [dlg, setDlg] = useState(null);

  const refresh = useCallback(() => ccApi.reports(status).then((d) => setReports(d.reports)).catch(() => {}), [status]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (target && ["post", "comment", "reel"].includes(target.type) && target.id) ccApi.content(target.type, target.id).then(setContent).catch((e) => toast.error(errMsg(e, "Content not found")));
    else setContent(null);
  }, [target]);

  const run = (spec, fn) => setDlg({ ...spec, fn });
  const submit = async ({ reason, confirm }) => {
    try {
      await dlg.fn({ reason, confirm });
      toast.success(`${dlg.title} — logged`);
      setDlg(null); refresh();
      if (content) ccApi.content(target.type, target.id).then(setContent).catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openTarget = (r) => {
    if (r.target_type === "user") setTarget({ type: "user", id: r.target_id, _tab: "member" });
    else if (r.target_type === "post") setTarget({ type: "post", id: r.target_id });
    else if (r.target_type === "live" || r.target_type === "live_chat") setTarget({ type: "live", id: r.target_id, _tab: "live" });
  };

  const c = content?.content;
  useEffect(() => {
    if (target?.evidence && c && me?.can?.evidence && !content.author?.is_owner) {
      setDlg({ title: "Capture evidence (screenshot)", cta: "Capture", fn: ({ reason }) => captureAndUpload({ target: { type: target.type, id: c.id, accountId: content.author?.id }, reasonCode: reason.slice(0, 60), selectorHint: `[data-post-id="${c.id}"]` }) });
    } else if (target?.evidence && c && me && !me?.can?.evidence) {
      toast.error("Evidence capture requires Safety Admin or owner access.");
    }
  }, [target?.evidence, c?.id, me?.can?.evidence]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      {c && (
        <Section title={`${target.type} · ${c.id.slice(0, 8)}…`} testid="cc-content-card" right={<button type="button" className="text-[11px] text-zinc-500 hover:text-white" data-testid="cc-content-back" onClick={() => setTarget(null)}>← reports</button>}>
          <div className="text-xs text-zinc-400">by <button type="button" className="text-white hover:underline" data-testid="cc-content-author" onClick={() => setTarget({ type: "user", id: content.author?.id, _tab: "member" })}>{content.author?.display_name}</button> · {fmtTs(c.created_at)} · {content.reports.length} report(s) {c.is_hidden && <Pill tone="red" testid="cc-content-hidden">hidden</Pill>}</div>
          {c.text && <div className="text-sm text-zinc-200 whitespace-pre-wrap border-l-2 border-white/10 pl-3">{c.text.slice(0, 500)}</div>}
          {c.media_url && <img src={c.media_url} alt="" className="max-h-40 rounded object-cover" />}
          {content.author?.is_owner ? <div className="text-xs text-amber-200">Owner content is protected.</div> : (
            <div className="flex flex-wrap gap-2">
              <ActionBtn tone={c.is_hidden ? "good" : "danger"} testid="cc-content-hide" onClick={() => run({ title: c.is_hidden ? "Unhide content" : "Hide content", cta: c.is_hidden ? "Unhide" : "Hide" }, ({ reason }) => ccApi.hideContent(target.type, c.id, { reason, hidden: !c.is_hidden }))}>{c.is_hidden ? "Unhide" : "Hide / remove"}</ActionBtn>
              {me?.can?.evidence && <ActionBtn testid="cc-content-evidence" onClick={() => run({ title: "Capture evidence (screenshot)", cta: "Capture" }, ({ reason }) => captureAndUpload({ target: { type: target.type, id: c.id, accountId: content.author?.id }, reasonCode: reason.slice(0, 60), selectorHint: `[data-post-id="${c.id}"]` }))}><Camera size={12} className="inline mr-1" />Evidence</ActionBtn>}
            </div>
          )}
        </Section>
      )}
      <Section title={`Reports · ${reports.length}`} testid="cc-reports" right={
        <div className="flex gap-1">{["open", "resolved", "all"].map((s) => <button key={s} type="button" data-testid={`cc-reports-filter-${s}`} onClick={() => setStatus(s)} className={`px-2 py-0.5 text-[11px] rounded-full ${status === s ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>{s}</button>)}</div>
      }>
        {reports.length === 0 && <Empty>No {status} reports.</Empty>}
        {reports.map((r) => (
          <div key={r.id} data-testid={`cc-report-${r.id}`} className="border-b border-white/5 last:border-0 pb-2 space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Pill tone="zinc">{r.target_type}</Pill>
              <span className="text-zinc-300">“{r.reason}”</span>
              <span className="text-zinc-500">by {r.reporter?.display_name} · {fmtTs(r.created_at)}</span>
              {r.status === "resolved" && <Pill tone="green">{r.resolution}</Pill>}
            </div>
            <div className="flex flex-wrap gap-2">
              <ActionBtn testid={`cc-report-open-${r.id}`} onClick={() => openTarget(r)}>Open target{r.target_user ? ` ${r.target_user.display_name}` : ""}</ActionBtn>
              {r.status === "open" && RESOLUTIONS.slice(0, 3).map((res) => (
                <ActionBtn key={res} tone={res === "dismissed" ? "default" : "warn"} testid={`cc-report-resolve-${res}-${r.id}`} onClick={() => run({ title: `Resolve report · ${res}`, cta: "Resolve" }, ({ reason }) => ccApi.resolveReport(r.id, { reason, resolution: res }))}>{res.replace("_", " ")}</ActionBtn>
              ))}
              {r.status === "open" && <ActionBtn tone="danger" testid={`cc-report-resolve-escalated-${r.id}`} onClick={() => run({ title: "Escalate to case review", cta: "Escalate" }, ({ reason }) => ccApi.resolveReport(r.id, { reason, resolution: "escalated" }))}>escalate</ActionBtn>}
            </div>
          </div>
        ))}
      </Section>
      <ReasonDialog spec={dlg} onClose={() => setDlg(null)} onSubmit={submit} />
    </div>
  );
}
